// /ts/features/collapse.ts
// ※ パスは実際の構成に合わせて調整してください
import { SlideController } from '../modules/animation/_Slide';

//////////////////////////////////////////////////
//// Type:
//////////////////////////////////////////////////
type CollapseState = 'open' | 'closed';

type CollapseItem = {
  slide: HTMLElement;
  trigger: HTMLElement;
  targets: HTMLElement[];
  container?: HTMLElement;
};

type CollapseItemInitializer = (item: CollapseItem) => void;

type CollapseModeConfig = {
  defaultSlide: string;
  defaultTrigger: string;
  defaultTarget: string;
  idPrefix: string;
  isDetailsMode: boolean;
  enableKeydown: boolean;
};

type CollapseApplyMode = 'buttons' | 'details';

//////////////////////////////////////////////////
//// Interface:
//////////////////////////////////////////////////
interface CollapseBindOptions
{
  slideSelector: string;
  triggerSelector: string;
  targetSelector: string;
  exclusive?: boolean;
}

type CollapseApplyOptions = Partial<CollapseBindOptions> & {
  mode?: CollapseApplyMode;

  /**
   * selector を1つでも指定した場合に、
   * 未指定の selector を “class側デフォルト” で補完するかどうか
   *
   * true（推奨）: 一部指定でも動作継続（不足は補完）
   * false: 一部指定を禁止（不足があれば return）
   */
  allowPartialSelectors?: boolean;
};

//////////////////////////////////////////////////
//// Class:
//////////////////////////////////////////////////
export class CollapsePanel
{
  private readonly mRoot: HTMLElement;
  private readonly mSlideController: SlideController;

  constructor(root: HTMLElement, duration: number = 500)
  {
    this.mRoot = root;
    this.mSlideController = new SlideController(duration);
  }

  //////////////////////////////////////////////////
  /// AREA: Public
  //////////////////////////////////////////////////

  /**
   * // Method: 統合 apply
   *
   * ルール：
   * - slideSelector / triggerSelector / targetSelector が 1つも渡されない → data-collapse-* デフォルトでバインド
   * - 1つでも渡される → selector 指定として扱う（不足は class側デフォルトで補完（allowPartialSelectors=true））
   */
  public apply(options: CollapseApplyOptions = {}): void
  {
    const mode: CollapseApplyMode = options.mode ?? 'details';
    const is_details_mode: boolean = mode === 'details';

    const enable_keydown: boolean = !is_details_mode;
    const exclusive: boolean = options.exclusive ?? false;

    const has_any_selector: boolean =
      typeof options.slideSelector === 'string'
      || typeof options.triggerSelector === 'string'
      || typeof options.targetSelector === 'string';

    const allow_partial: boolean = options.allowPartialSelectors ?? true;

    const data_defaults = this.getDefaults('data', mode);
    const class_defaults = this.getDefaults('class', mode);

    const defaults = has_any_selector ? class_defaults : data_defaults;

    // selector 指定があるのに、一部だけ欠けている場合の扱い
    if (has_any_selector && !allow_partial)
    {
      const lacks_some_selector: boolean =
        typeof options.slideSelector !== 'string'
        || typeof options.triggerSelector !== 'string'
        || typeof options.targetSelector !== 'string';

      if (lacks_some_selector)
      {
        return;
      }
    }

    const bind_options: CollapseBindOptions = {
      slideSelector: options.slideSelector ?? defaults.slide,
      triggerSelector: options.triggerSelector ?? defaults.trigger,
      targetSelector: options.targetSelector ?? defaults.target,
      exclusive,
    };

    this.applyCollapse(bind_options, {
      defaultSlide: bind_options.slideSelector,
      defaultTrigger: bind_options.triggerSelector,
      defaultTarget: bind_options.targetSelector,
      idPrefix: defaults.idPrefix,
      isDetailsMode: is_details_mode,
      enableKeydown: enable_keydown,
    });
  }

  /**
   * // Method: 互換用（details版）
   * （既存コードを壊さないために残しています）
   */
  public applyDetails(options: Partial<CollapseBindOptions> = {}): void
  {
    this.apply({ mode: 'details', ...options });
  }

  /**
   * // Method: 互換用（button版）
   */
  public applyButtons(options: Partial<CollapseBindOptions> = {}): void
  {
    this.apply({ mode: 'buttons', ...options });
  }

  //////////////////////////////////////////////////
  /// AREA: Internal Defaults
  //////////////////////////////////////////////////

  private getDefaults(kind: 'data' | 'class', mode: CollapseApplyMode):
    {
      slide: string;
      trigger: string;
      target: string;
      idPrefix: string;
    }
  {
    const is_details_mode: boolean = mode === 'details';

    if (kind === 'data')
    {
      if (is_details_mode)
      {
        return {
          slide: 'details[data-collapse-slide]',
          trigger: 'summary',
          target: '[data-collapse-target]',
          idPrefix: 'collapse-data-details',
        };
      }

      return {
        slide: '[data-collapse-slide]',
        trigger: '[data-collapse-trigger]',
        target: '[data-collapse-target]',
        idPrefix: 'collapse-data-btn',
      };
    }

    // kind === 'class'
    if (is_details_mode)
    {
      return {
        slide: 'details',
        trigger: 'summary',
        target: '.content',
        idPrefix: 'collapse-details',
      };
    }

    return {
      slide: '.details',
      trigger: '.summary',
      target: '.content',
      idPrefix: 'collapse-btn',
    };
  }

  //////////////////////////////////////////////////
  /// AREA: Core Apply
  //////////////////////////////////////////////////

  private applyCollapse(options: Partial<CollapseBindOptions>, modeConfig: CollapseModeConfig): void
  {
    const {
      defaultSlide,
      defaultTrigger,
      defaultTarget,
      idPrefix,
      isDetailsMode,
      enableKeydown,
    } = modeConfig;

    const {
      slideSelector = defaultSlide,
      triggerSelector = defaultTrigger,
      targetSelector = defaultTarget,
      exclusive = false,
    } = options;

    const initializer: CollapseItemInitializer = (item: CollapseItem) =>
    {
      // ✅共通：hidden 属性を除去し、display:none へ正規化（SlideController で確実に開閉できるように）
      item.targets.forEach((target: HTMLElement) =>
      {
        if (target.hasAttribute('hidden'))
        {
          target.removeAttribute('hidden');
        }

        // 初期は閉じる前提（必要なら data- で拡張可）
        target.style.display = 'none';
      });

      // ✅details mode の場合のみ、container を details に紐付け & open を外す
      if (isDetailsMode)
      {
        const is_real_details: boolean = item.slide.tagName.toLowerCase() === 'details';
        if (!is_real_details) return;

        item.slide.removeAttribute('open');
        item.container = item.slide;
      }
    };

    const { items } = this.collectSlideItems(
      slideSelector,
      triggerSelector,
      targetSelector,
      initializer
    );

    this.setupSlideItems(items, idPrefix);

    this.attachEventHandlers(items, { isDetailsMode, exclusive, enableKeydown });
  }

  private attachEventHandlers(
    items: CollapseItem[],
    config: { isDetailsMode: boolean; exclusive: boolean; enableKeydown: boolean; }
  ): void
  {
    const { isDetailsMode, exclusive, enableKeydown } = config;

    items.forEach((item: CollapseItem) =>
    {
      const { slide, trigger, targets } = item;
      const primary_target: HTMLElement | undefined = targets[0];
      if (!primary_target) return;

      trigger.addEventListener('click', (ev) =>
      {
        const tag_name: string = trigger.tagName.toLowerCase();
        const is_link_with_href: boolean = tag_name === 'a'
          && (trigger as HTMLAnchorElement).hasAttribute('href');

        const is_hidden = this.mSlideController.isHidden(primary_target);

        if (isDetailsMode)
        {
          ev.preventDefault();
          ev.stopPropagation();
        }

        // buttons mode でも a[href] は遷移させない
        if (!isDetailsMode && is_link_with_href)
        {
          ev.preventDefault();
        }

        if (exclusive && is_hidden)
        {
          this.closeOthers(items, item);
        }

        if (isDetailsMode)
        {
          if (is_hidden)
          {
            slide.setAttribute('open', '');

            targets.forEach((target: HTMLElement) =>
            {
              this.mSlideController.slideDown(target);
            });
          } else
          {
            targets.forEach((target: HTMLElement) =>
            {
              this.mSlideController.slideUp(target);
            });
          }
        } else
        {
          targets.forEach((target: HTMLElement) =>
          {
            this.mSlideController.slideToggle(target);
          });
        }
      });

      if (enableKeydown)
      {
        const tag_name: string = trigger.tagName.toLowerCase();
        const is_native_button: boolean = tag_name === 'button';
        const is_summary: boolean = tag_name === 'summary';
        const is_link_with_href: boolean = tag_name === 'a'
          && (trigger as HTMLAnchorElement).hasAttribute('href');

        // ✅ネイティブ要素はブラウザが Enter/Space を処理するため、二重発火を避ける
        const should_attach_keydown: boolean = !(is_native_button || is_link_with_href || is_summary);

        if (should_attach_keydown)
        {
          trigger.addEventListener('keydown', (ev: KeyboardEvent) =>
          {
            if (ev.key === 'Enter' || ev.key === ' ')
            {
              ev.preventDefault();
              trigger.click();
            }
          });
        }
      }
    });
  }

  //////////////////////////////////////////////////
  /// AREA: Common
  //////////////////////////////////////////////////

  private collectSlideItems(
    slide_element: string,
    trigger_element: string,
    target_element: string,
    initializer?: CollapseItemInitializer
  ): { items: CollapseItem[]; }
  {
    const slide_list: NodeListOf<HTMLElement> = this.mRoot.querySelectorAll<HTMLElement>(slide_element);
    const items: CollapseItem[] = [];

    slide_list.forEach((slide: HTMLElement) =>
    {
      const trigger: HTMLElement | null = slide.querySelector<HTMLElement>(trigger_element);
      const target_list: HTMLElement[] = Array.from(
        slide.querySelectorAll<HTMLElement>(target_element)
      );

      if (!trigger || target_list.length === 0) return;

      const item: CollapseItem = { slide, trigger, targets: target_list };

      if (initializer)
      {
        initializer(item);
      }

      items.push(item);
    });

    return { items };
  }

  private setupSlideItems(items: CollapseItem[], prefix: string): void
  {
    items.forEach((item: CollapseItem, index: number) =>
    {
      const { trigger, targets, slide, container } = item;

      const primary_target: HTMLElement | undefined = targets[0];
      if (!primary_target) return;

      const target_id: string = primary_target.id || `${prefix}-${index}`;
      primary_target.id = target_id;

      trigger.setAttribute('aria-controls', target_id);
      trigger.setAttribute('aria-expanded', 'false');

      const tag_name: string = trigger.tagName.toLowerCase();
      const is_native_button: boolean = tag_name === 'button';
      const is_link_with_href: boolean = tag_name === 'a' && (trigger as HTMLAnchorElement).hasAttribute('href');
      const is_summary: boolean = tag_name === 'summary';

      if (!is_native_button && !is_link_with_href && !is_summary)
      {
        trigger.setAttribute('role', 'button');

        if (!trigger.hasAttribute('tabindex'))
        {
          trigger.tabIndex = 0;
        }
      }

      const icon_list: NodeListOf<HTMLElement> = trigger.querySelectorAll<HTMLElement>('[data-toggle-icon]');
      icon_list.forEach((icon: HTMLElement) =>
      {
        icon.setAttribute('aria-hidden', 'true');
      });

      // ✅「閉じる」ボタン（任意）
      // data-collapse-close があれば、そのクリックで trigger をトグルする
      const close_list: NodeListOf<HTMLElement> = slide.querySelectorAll<HTMLElement>('[data-collapse-close]');
      close_list.forEach((close: HTMLElement) =>
      {
        close.addEventListener('click', (ev) =>
        {
          ev.preventDefault();
          trigger.click();
        });
      });

      primary_target.addEventListener('slidetoggle:end', (ev: Event) =>
      {
        const event = ev as CustomEvent<{ state: CollapseState; }>;
        const state: CollapseState | undefined = event.detail?.state;
        if (!state) return;

        trigger.setAttribute('aria-expanded', state === 'open' ? 'true' : 'false');

        if (container)
        {
          if (state === 'open')
          {
            container.setAttribute('open', '');
          } else
          {
            container.removeAttribute('open');
          }
        }
      });
    });
  }

  private closeOthers(items: CollapseItem[], current: CollapseItem): void
  {
    items.forEach((item: CollapseItem) =>
    {
      if (item === current) return;

      item.targets.forEach((target: HTMLElement) =>
      {
        if (!this.mSlideController.isHidden(target))
        {
          this.mSlideController.slideUp(target);
        }
      });
    });
  }
}
